import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {select, Store} from '@ngrx/store';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest as observableCombineLatest, forkJoin, Observable, of as observableOf, of, throwError} from 'rxjs';

import {User} from '../model/user';
import {environment} from '@env/environment';
import {AuthenticatedUserService} from './authenticated-user.service';
import {getAuthenticatedUserPreferences, State} from '@app/app.reducers';
import {App, Org, PreferenceItem, Preferences} from '../model/user-preferences.model';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {PIIShieldService} from '@app/core/service/pii-shield.service';
import {PII_LEVEL} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';

@Injectable()
export class UserPreferencesService {
    preferences$: Observable<Preferences>;
    orgs: Observable<Org[]>;
    apps: Observable<Application[]>;

    private _preferences: BehaviorSubject<Preferences>;
    private _orgs: BehaviorSubject<Org[]>;
    private _apps: BehaviorSubject<Application[]>;

    private dataStore: {
        preferences: Preferences;
        orgs: Org[];
        apps: Application[];
    };

    constructor(private authenticatedUserService: AuthenticatedUserService,
                private store: Store<State>,
                private http: HttpClient,
                private piiShieldService: PIIShieldService,
                private snackBar: MatSnackBar
    ) {
        this.dataStore = {
            preferences: new Preferences(),
            orgs: <Org[]>[],
            apps: <Application[]>[]
        };

        this._preferences = <BehaviorSubject<Preferences>>new BehaviorSubject(new Preferences());
        this._orgs = <BehaviorSubject<Org[]>>new BehaviorSubject(<Org[]>[]);
        this._apps = <BehaviorSubject<Application[]>>new BehaviorSubject(<Application[]>[]);

        this.preferences$ = this._preferences.asObservable();
        this.orgs = this._orgs.asObservable();
        this.apps = this._apps.asObservable();

        this.store.pipe(select(getAuthenticatedUserPreferences))
            .subscribe(preferences => {
                this.dataStore.preferences = preferences;
                this._preferences.next(preferences);
            });
    }

    private loadPreferences(): Observable<Preferences> {
        return this.http.get<Preferences>(`${environment.datahubBaseURI}/preferences/selectors`)
            .pipe(
                map((data: Preferences) => new Preferences(data)),
                // .map((preferences: Preferences) => {
                //     this.dataStore.preferences = preferences;
                //     this._preferences.next(Object.assign({}, this.dataStore).preferences);
                //     return preferences;
                // })
                catchError(this.handleError('Could not load preferences.'))
            );
    }

    updatePreferences(preferences: Preferences): Observable<Preferences> {
        return this.http.post<Preferences>(`${environment.datahubBaseURI}/preferences/selectors`, preferences);
        // .subscribe((data: Preferences) => {
        //     Object.assign(this.dataStore.preferences, data);
        //
        //     this._preferences.next(Object.assign({}, this.dataStore).preferences);
        // }, error => console.log('Could not update preferences.'));
    }

    updatePreferencesItem(key: string, value: PreferenceItem): Observable<Preferences> {
        const preferences = new Preferences();
        preferences[key] = [value];
        return this.updatePreferences(preferences);
    }

    getUserExtraData(user: User): Observable<[Preferences, Application[] | null, Preferences | null]> {

        const preferences$ = this.loadPreferences();

        if (this.authenticatedUserService.isVendor(user)) {

            const vendorAppsS = this.loadVendorApps(user);
            const vendorApplicationsPIILevel$ = this.piiShieldService.getVendorApplicationsPIILevel(user.orgGuid);

            return forkJoin([
                preferences$.pipe(catchError((error) => this.showError(error))),
                vendorAppsS.pipe(catchError((error) => this.showError(error))),
                vendorApplicationsPIILevel$.pipe(catchError((error) => this.showError(error))),
            ])
                .pipe(
                    map(([userPreferences, userApplications, vendorApplicationsPIILevel]: [Preferences, Application[], { [key: string]: PII_LEVEL }]) => {
                        const currentAppId = (userApplications?.length > 0)
                            ? userPreferences.getPreferencesItemValue('currentApp')
                            : '';
                        let $updateUserPreferences: Observable<Preferences> = observableOf(userPreferences);

                        if (userApplications.length > (-1)) {
                            const currentApp = userApplications.find((app: Application) => app.guid === currentAppId);
                            if (!currentApp) {
                                const userPreferenceItem = new PreferenceItem(userApplications[0]?.name || '', userApplications[0]?.guid || '');

                                userPreferences['currentApp'] = [userPreferenceItem];
                                $updateUserPreferences = this.updatePreferencesItem('currentApp', userPreferenceItem);
                            }

                            userApplications = userApplications.map((app: Application) => {
                                app.piiLevel = vendorApplicationsPIILevel[app.guid];
                                return app;
                            });
                        }

                        return {userPreferences, userApplications, $updateUserPreferences};
                    }),
                    mergeMap(o => observableCombineLatest(
                        // this.loadOrgs(`${environment.datahubBaseURI}/api/v2/directory/organizations`),
                        observableOf(o.userPreferences),
                        observableOf(o.userApplications),
                        o.$updateUserPreferences
                    ))
                );
        } else {
            return observableCombineLatest(
                // this.loadOrgs(`${environment.datahubBaseURI}/api/v2/directory/organizations`),
                preferences$,
                observableOf(null),
                observableOf(null)
            );
        }
    }

    private showError(msg: string, defaultResult: any = []): Observable<any> {
        this.snackBar.open(msg, '', <MatSnackBarConfig>{duration: 3000});
        return of(defaultResult);
    }

    private loadOrgs(url: string): Observable<Org[]> {
        return this.http.get<Org[]>(url)
            .pipe(
                map((orgs: Org[]) => {
                    this.dataStore.orgs = orgs;
                    this._orgs.next(Object.assign({}, this.dataStore).orgs);
                    return orgs;
                }),
                catchError(this.handleError('Could not load orgs.'))
            );
    }

    loadVendorApps(user: User): Observable<Application[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/vendor/${user.orgGuid}`;
        return this.http.get<Application[]>(url)
            .pipe(
                map((apps: Application[]) => {
                    this.dataStore.apps = apps;
                    this._apps.next(Object.assign({}, this.dataStore).apps);
                    return apps;
                }),
                catchError(this.handleError('Could not load apps.'))
            );
    }

    private handleError(operation: string) {
        return (err: any) => {
            const errMsg = `error in ${operation}()`;
            console.log(`${errMsg}:`, err);
            if (err instanceof HttpErrorResponse) {
                // you could extract more info about the error if you want, e.g.:
                console.log(`status: ${err.status}, ${err.statusText}`);
                // errMsg = ...
            }
            return throwError(errMsg);
        };
    }
}
